import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { 
  TrendingUp, 
  Clock, 
  Zap, 
  Users,
  CheckCircle,
  BarChart3
} from 'lucide-react';
import api from '../services/api';

const AnalyticsPage = () => {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, ticketsData] = await Promise.all([
        api.getDashboardStats(),
        api.getTickets()
      ]);
      setStats(statsData);
      setTickets(ticketsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-gray-600">Loading analytics...</div>
      </div>
    );
  }

  const categoryCounts = tickets.reduce((acc, ticket) => {
    acc[ticket.category] = (acc[ticket.category] || 0) + 1;
    return acc;
  }, {});

  const topCategories = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Analytics</h2>
        <p className="text-gray-600 mt-1">Performance metrics and insights</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="border-gray-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                +12%
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.total_tickets}</div>
            <div className="text-sm text-gray-600 mt-1">Total Tickets</div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Zap className="w-5 h-5 text-green-600" />
              <Badge className="bg-green-100 text-green-800 border-green-200">
                Live
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.ai_handled_percent}%</div>
            <div className="text-sm text-gray-600 mt-1">AI Resolution Rate</div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Clock className="w-5 h-5 text-orange-600" />
              <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                -40%
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.avg_resolution_time}</div>
            <div className="text-sm text-gray-600 mt-1">Avg Resolution Time</div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CheckCircle className="w-5 h-5 text-cyan-600" />
              <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200">
                Today
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.resolved_today}</div>
            <div className="text-sm text-gray-600 mt-1">Resolved Today</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-2 gap-6">
        {/* Top Categories */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle>Top Ticket Categories</CardTitle>
            <CardDescription>Most common support requests</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {topCategories.map(([category, count], idx) => (
              <div key={category} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 font-medium">{category}</span>
                  <span className="text-gray-600">{count} tickets</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full"
                    style={{ width: `${(count / tickets.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Status Breakdown */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle>Ticket Status Distribution</CardTitle>
            <CardDescription>Current ticket statuses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">Resolved</span>
                <span className="text-green-600 font-semibold">
                  {tickets.filter(t => t.status === 'resolved').length} tickets
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${(tickets.filter(t => t.status === 'resolved').length / tickets.length) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">In Progress</span>
                <span className="text-blue-600 font-semibold">
                  {tickets.filter(t => t.status === 'in_progress').length} tickets
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: `${(tickets.filter(t => t.status === 'in_progress').length / tickets.length) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">Open</span>
                <span className="text-yellow-600 font-semibold">
                  {tickets.filter(t => t.status === 'open').length} tickets
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full"
                  style={{ width: `${(tickets.filter(t => t.status === 'open').length / tickets.length) * 100}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <div className="grid grid-cols-3 gap-6">
        <Card className="border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">Excellent Performance</CardTitle>
                <CardDescription>AI handling 82% of tickets</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700">
              Your AI resolution rate is 32% higher than industry average
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-gradient-to-br from-blue-50 to-cyan-50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">Fast Response</CardTitle>
                <CardDescription>Avg {stats.avg_resolution_time}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700">
              40% faster than last month's average
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">Happy Users</CardTitle>
                <CardDescription>High satisfaction rate</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700">
              95% of users satisfied with resolution speed
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsPage;
